import { Link } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import { Rating } from "@/components/marketplace/Reviews";
import { FavoriteButton } from "@/components/FavoriteButton";
import { FeaturedBadge } from "@/components/featured/FeaturedBadge";
import { useFeaturedImpression, featuredClickProps } from "@/components/featured/featuredMetrics";

export function VerifiedBadge({ verified, size = "sm", testid = "verified-badge" }) {
  if (!verified) return null;
  return <span data-testid={testid} title="Loja verificada pela ANGOLAUK+." className={`inline-flex items-center gap-0.5 font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full ${size === "lg" ? "text-xs px-2 py-0.5" : "text-[10px] px-1.5 py-px"}`}><BadgeCheck size={size === "lg" ? 13 : 10} /> Loja Verificada</span>;
}

export function money(minor, currency) {
  const cur = typeof currency === "string" && /^[A-Za-z]{3}$/.test(currency) ? currency.toUpperCase() : "GBP";
  const n = Number(minor);
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: cur }).format((Number.isFinite(n) ? n : 0) / 100);
}

export const PRODUCT_STATUS_LABEL = {
  draft: "Rascunho", pending_approval: "Em revisão", published: "Publicado", rejected: "Rejeitado",
  suspended: "Suspenso", sold_out: "Esgotado", archived: "Arquivado",
};
export const SELLER_STATUS_LABEL = {
  pending: "Em revisão", approved: "Aprovada", rejected: "Rejeitada", suspended: "Suspensa", closed: "Encerrada",
};
export const STATUS_TONE = {
  published: "bg-green-100 text-green-700", approved: "bg-green-100 text-green-700",
  pending_approval: "bg-yellow-100 text-yellow-800", pending: "bg-yellow-100 text-yellow-800", draft: "bg-zinc-100 text-zinc-600",
  rejected: "bg-red-100 text-red-700", suspended: "bg-red-100 text-red-700", closed: "bg-zinc-200 text-zinc-700",
  sold_out: "bg-orange-100 text-orange-800", archived: "bg-zinc-200 text-zinc-700",
};

export function StatusPill({ status, map }) {
  return <span data-testid={`status-${status}`} className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_TONE[status] || "bg-zinc-100"}`}>{(map || PRODUCT_STATUS_LABEL)[status] || status}</span>;
}

export function ProductCard({ p }) {
  const ref = useFeaturedImpression("product", p.id, !!p.featured_active);
  return (
    <Link ref={ref} to={`/marketplace/produto/${p.id}`} data-testid={`product-card-${p.id}`} data-featured={p.featured_active ? "1" : "0"} {...featuredClickProps(p, "product")}
       className="bg-white rounded-2xl border border-zinc-200 overflow-hidden card-hover block">
      <div className="relative aspect-square bg-zinc-100">
        {p.photos?.[0] && <img src={p.photos[0]} alt={p.name} className="w-full h-full object-cover" />}
        {p.featured_active && <FeaturedBadge onDark className="absolute top-2 left-2" testid={`featured-badge-product-${p.id}`} />}
      </div>
      <div className="p-3">
        <div className="font-semibold text-sm truncate flex items-center justify-between gap-1"><span className="truncate">{p.name}</span><FavoriteButton type="product" id={p.id} /></div>
        <div className="text-[11px] text-zinc-500 truncate">{p.seller_name} · {p.city}</div>
        {p.seller_verified && <div className="mt-0.5"><VerifiedBadge verified testid={`verified-badge-${p.id}`} /></div>}
        <Rating avg={p.rating_avg} count={p.rating_count} testid={`rating-${p.id}`} />
        <div className="flex items-center justify-between mt-1">
          <span className="font-display font-extrabold">{money(p.price_minor, p.currency)}</span>
          {p.status === "sold_out" && <span className="text-[10px] font-bold text-orange-700">Esgotado</span>}
        </div>
      </div>
    </Link>
  );
}
